import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { RuleInterface, SleepWaitConditions } from '@vendasta/automata';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Subject } from 'rxjs';
import {
  convertTimeUnit,
  DelayControlComponent,
  TimeUnit,
} from '../../../common/controls/delay-control/delay-control.component';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { WeekdaySelectorComponent } from '../../../common/controls/weekday-selector.component';
import { RouterLink } from '@angular/router';
import { TimeRangeSliderComponent } from '../../../common/controls/time-range-slider.component';
import { MonthDaySelectorComponent } from '../../../common/controls/monthday-selector.component';

const waitUntilEnabled = 'waitUntilEnabled';
const monthDayEnabled = 'monthDayEnabled';
const weekdayEnabled = 'weekdayEnabled';
const timeRangeEnabled = 'timeRangeEnabled';

@Component({
  selector: 'automata-delay-panel',
  templateUrl: './delay-panel.component.html',
  styleUrls: ['./delay-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    DelayControlComponent,
    MatListModule,
    MatCheckboxModule,
    WeekdaySelectorComponent,
    RouterLink,
    TimeRangeSliderComponent,
    MonthDaySelectorComponent,
  ],
})
export class DelayPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy, OnChanges {
  destroyed$$ = new Subject<void>();

  form: UntypedFormGroup = this.formBuilder.group({
    waitUntilEnabled: false,
    monthDayEnabled: false,
    weekdayEnabled: false,
    timeRangeEnabled: false,
  });

  constructor(readonly formBuilder: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    // There's nothing asynchronous here, so we can safely enable the save button.
    this.savingEnabled = true;
    this.toggleCheckboxes();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('step' in changes) {
      this.toggleCheckboxes();
    }
  }

  private toggleCheckboxes() {
    this.toggleShowingWaitUntil();
    this.toggleShowingMonthDays();
    this.toggleShowingWeekdays();
    this.toggleShowingTimeRange();
  }

  private toggleShowingWaitUntil() {
    const show = !!this.step?.sleep;
    this.form.get(waitUntilEnabled)!.setValue(show);
  }

  private toggleShowingMonthDays() {
    const show = (this.step?.sleepWaitConditions?.dayOfMonthSelection || []).length > 0;
    this.form.get(monthDayEnabled)!.setValue(show);
  }

  private toggleShowingWeekdays() {
    const show = (this.step?.sleepWaitConditions?.weekdaySelection || []).length > 0;
    this.form.get(weekdayEnabled)!.setValue(show);
  }

  private toggleShowingTimeRange() {
    const cs = this.step?.sleepWaitConditions;
    const show =
      (cs?.timeRangeStartHour || 0) > 0 ||
      (cs?.timeRangeStartMinute || 0) > 0 ||
      (cs?.timeRangeEndHour || 0) > 0 ||
      (cs?.timeRangeEndMinute || 0) > 0;
    this.form.get(timeRangeEnabled)!.setValue(show);
  }

  private isPanelSavable(): boolean {
    return (
      this.form.get(waitUntilEnabled)?.value === true ||
      this.form.get(monthDayEnabled)?.value === true ||
      this.form.get(weekdayEnabled)?.value === true ||
      this.form.get(timeRangeEnabled)?.value === true
    );
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    return {};
  }

  getSleepTimeInNanos(): number {
    if (this.form.get(waitUntilEnabled)?.value === false) {
      return 0;
    }

    // Pick a good default if the form isn't ready. Assume it's in hours.
    if (!this.form) {
      return convertTimeUnit(this.step?.sleep || 0, TimeUnit.Hour, TimeUnit.Nano);
    }

    // Selected time may be in days/other unit, so ensure it's converted to nanos.
    return convertTimeUnit(
      this.form.controls?.delay?.get('sleep')?.value,
      this.form.controls?.delay?.get('timeUnit')?.value,
      TimeUnit.Nano,
    );
  }

  getSleepWaitConditions(): SleepWaitConditions {
    const conditions = new SleepWaitConditions({});
    if (this.form.get(monthDayEnabled)?.value === true) {
      conditions.dayOfMonthSelection = this.form.get('sleepMonthDays')?.value || [];
    }
    if (this.form.get(weekdayEnabled)?.value === true) {
      conditions.weekdaySelection = this.form.get('sleepWeekdays')?.value || [];
    }
    if (this.form.get(timeRangeEnabled)?.value === true) {
      conditions.timeRangeStartHour = this.form.get('sleepTimeRangeStartHour')?.value || 0;
      conditions.timeRangeStartMinute = this.form.get('sleepTimeRangeStartMinute')?.value || 0;
      conditions.timeRangeEndHour = this.form.get('sleepTimeRangeEndHour')?.value || 0;
      conditions.timeRangeEndMinute = this.form.get('sleepTimeRangeEndMinute')?.value || 0;
    }
    return conditions;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  isFormValid(): boolean | string {
    if (!this.isPanelSavable()) {
      return 'AUTOMATIONS.EDITOR.TASKS.DELAY.MUST_SELECT_ONE';
    }

    return this.form.valid;
  }
}
